//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ControlBarVisibility, OmakasePlayer, PlayerChromingTheme } from '@byomakase/omakase-player';
import useComponentOverride from '@/components/_hooks/useComponentOverride';
import PolarisIcon from '../PolarisIcon';
import { alpha, lighten, Grid, MenuItem, Select, SelectChangeEvent, useTheme, Box, Button } from '@mui/material';
import useVideoPlayerAdvanced from './useVideoPlayerAdvanced';
import VolumeController from './VolumeController';
import MarkerEditorModal from './MarkerEditorModal';
import { ContentPlaybackTimelineAudioTrack, CustomMarker, FileWithProxy } from './video-player.types';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OmakasePlayerClientConfig, OmakasePlayerFeature } from '@/__configs/config_types';
import { useToast } from '../Toast';
import useFileType from '@/components/_hooks/useFileType';

export type VideoPlayerProps = {
  url: string | null | undefined;
  poster?: string;
  autoplay?: boolean;
  id?: string;
  stopPropagation?: boolean;
  externalAssets?: FileWithProxy[];
  disableInventoryModal?: boolean;
  audioTracks?: ContentPlaybackTimelineAudioTrack[];
  onShowInventoryModal?: () => void;
  // if dolby rasp is enabled we want to wait for the url until we have the one from dolby,
  // otherwise the player will initialize with the default url and have to reload when the dolby url is ready
  waitForUrl?: boolean;
};

const detectProtocol = (url: string) => {
  if (!url) return undefined;

  const lower = url.toLowerCase();

  if (lower.includes('.m3u8')) {
    return 'hls';
  }

  if (lower.includes('.mp3') || lower.includes('.aac') || lower.includes('.ogg')) {
    return 'audio';
  }

  return 'native';
};

// Custom icon that shows both up and down triangles for Select's right-hand side
const UpDownIcon = ({ className }: { className?: string }) => (
  <span
    className={className}
    style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 0 }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path
        d="M7.3787 4.04581C7.41776 4.00707 7.44876 3.96099 7.46991 3.91022C7.49106 3.85944 7.50195 3.80498 7.50195 3.74997C7.50195 3.69497 7.49106 3.64051 7.46991 3.58973C7.44876 3.53896 7.41776 3.49288 7.3787 3.45414L5.29537 1.37081C5.25664 1.33175 5.21055 1.30076 5.15978 1.2796C5.109 1.25845 5.05454 1.24756 4.99954 1.24756C4.94453 1.24756 4.89007 1.25845 4.8393 1.2796C4.78852 1.30076 4.74244 1.33175 4.7037 1.37081L2.62037 3.45414C2.58132 3.49288 2.55032 3.53896 2.52917 3.58973C2.50801 3.64051 2.49712 3.69497 2.49712 3.74997C2.49712 3.80498 2.50801 3.85944 2.52917 3.91022C2.55032 3.96099 2.58132 4.00707 2.62037 4.04581C2.65911 4.08486 2.70519 4.11586 2.75596 4.13701C2.80674 4.15817 2.8612 4.16906 2.9162 4.16906C2.97121 4.16906 3.02567 4.15817 3.07644 4.13701C3.12722 4.11586 3.1733 4.08486 3.21204 4.04581L4.99954 2.25414L6.78704 4.04581C6.82577 4.08486 6.87186 4.11586 6.92263 4.13701C6.97341 4.15817 7.02787 4.16906 7.08287 4.16906C7.13788 4.16906 7.19234 4.15817 7.24311 4.13701C7.29389 4.11586 7.33997 4.08486 7.3787 4.04581ZM5.29537 8.62914L7.3787 6.54581C7.41755 6.50696 7.44837 6.46084 7.4694 6.41008C7.49042 6.35932 7.50124 6.30492 7.50124 6.24997C7.50124 6.13902 7.45716 6.0326 7.3787 5.95414C7.33985 5.91529 7.29373 5.88448 7.24297 5.86345C7.19222 5.84242 7.13781 5.8316 7.08287 5.8316C6.97191 5.8316 6.8655 5.87568 6.78704 5.95414L4.99954 7.74581L3.21204 5.95414C3.1733 5.91509 3.12722 5.88409 3.07644 5.86294C3.02567 5.84178 2.97121 5.83089 2.9162 5.83089C2.8612 5.83089 2.80674 5.84178 2.75596 5.86294C2.70519 5.88409 2.65911 5.91509 2.62037 5.95414C2.58132 5.99288 2.55032 6.03896 2.52917 6.08973C2.50801 6.14051 2.49712 6.19497 2.49712 6.24997C2.49712 6.30498 2.50801 6.35944 2.52917 6.41022C2.55032 6.46099 2.58132 6.50707 2.62037 6.54581L4.7037 8.62914C4.74244 8.66819 4.78852 8.69919 4.8393 8.72035C4.89007 8.7415 4.94453 8.75239 4.99954 8.75239C5.05454 8.75239 5.109 8.7415 5.15978 8.72035C5.21055 8.69919 5.25664 8.66819 5.29537 8.62914Z"
        fill="#EBF0FF"
      />
    </svg>
  </span>
);

const isTypingTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  if (!element) return false;
  if (element.isContentEditable) return true;

  const tag = element.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || tag === 'button';
};

const VideoPlayerAdvanced = ({
  audioTracks,
  url,
  waitForUrl = true,
  poster,
  autoplay = true,
  id = 'player',
  stopPropagation = false,
  disableInventoryModal = false,
  onShowInventoryModal,
  externalAssets = []
}: VideoPlayerProps) => {
  const { error } = useToast();
  const config = useConfigValue<OmakasePlayerClientConfig>(['omakasePlayerConfig']);
  const [hasError, setHasError] = useState<boolean>(false);
  const [showMarkerModal, setShowMarkerModal] = useState<boolean>(false);
  const [player, setPlayer] = useState<OmakasePlayer | null>(null);
  const [hasStartedLoading, setHasStartedLoading] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [focusedMarkerId, setFocusedMarkerId] = useState<string | undefined>(undefined);
  const [markerModalTrigger, setMarkerModalTrigger] = useState<'control' | 'in-keypress'>('control');
  const [markerModalData, setMarkerModalData] = useState<CustomMarker | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const resolvedPoster = poster || theme.preview_placeholder;
  const getFileGroup = useFileType();
  const [customMarkers, setCustomMarkers] = useState<CustomMarker[]>([]);
  const externalSubtitles = useMemo(() => {
    return externalAssets.filter((marker) => getFileGroup(marker.file_type) === 'subtitle');
  }, [externalAssets]);
  const customAudio = useMemo(() => {
    if (audioTracks?.length) {
      // use the ones from dolby
      return;
    }
    return externalAssets.find((marker) => getFileGroup(marker.file_type) === 'audio');
  }, [externalAssets, audioTracks]);
  const hasCustomMarkers = config?.features?.includes(OmakasePlayerFeature.CUSTOM_MARKERS);
  const { createTimeline, lanes } = useVideoPlayerAdvanced({
    customMarkers,
    focusedMarkerId,
    onMarkerFocus: setFocusedMarkerId,
    externalSubtitles,
    blackSegmentsVttUrl: '', // no data yet
    pointsOfInterestVttUrl: '', // no data yet
    thumbnailsVttUrl: '', // no data yet
    customAudio,
    audioTracks
  });

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    // Initialize player
    const newPlayer = new OmakasePlayer({
      playerHTMLElementId: id,
      playerChroming: {
        theme: PlayerChromingTheme.Default,
        themeConfig: {
          controlBarVisibility: ControlBarVisibility.FullscreenOnly
        }
      }
    });
    setPlayer(newPlayer);
    newPlayer
      .createTimeline({
        timelineHTMLElementId: `omakase-timeline-${id}`,
        zoomWheelEnabled: false,
        style: {
          stageMinHeight: 200,
          leftPaneWidth: 210,
          scrollbarHeight: 10,
          footerHeight: 12,
          scrollbarHandleBarFill: theme.palette.default.dark,
          scrubberTextFill: theme.palette.default.light,
          playheadTextFill: theme.palette.default.light,
          playheadPlayProgressFill: theme.palette.default.light,
          scrubberSnappedFill: theme.palette.default.light,
          playheadBufferedFill: 'transparent',
          footerBackgroundFill: theme.palette.default.light,
          backgroundFill: 'transparent',
          playheadFill: lighten(theme.palette.text.primary, 0.7)
        }
      })
      .subscribe({
        next: () => {
          createTimeline(newPlayer);

          newPlayer.video.onPlay$.subscribe(() => {
            setIsPlaying(true);
          });
          newPlayer.video.onPause$.subscribe(() => {
            setIsPlaying(false);
          });
        }
      });

    newPlayer.video.onVideoLoading$.subscribe({
      next: () => {
        setHasStartedLoading(true);
      }
    });

    // Cleanup on unmount
    return () => {
      newPlayer.destroy();
    };
  }, []);

  useEffect(() => {
    if ((!url && waitForUrl) || !player || hasStartedLoading) return;

    const protocol = detectProtocol(url);
    // Load a video
    player
      .loadVideo(url, {
        poster: poster || theme.preview_placeholder,
        protocol
      })
      .subscribe({
        next: () => {
          if (autoplay) {
            player.video.play();
          }
        },
        error: () => setHasError(true)
      });
  }, [url, waitForUrl, player, autoplay, hasStartedLoading]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  const onFfBack = () => {
    if (!player) return;
    player.video.pause().subscribe({
      next: () => {
        let frame = player?.video?.getCurrentFrame() || 0;
        if (frame < 10) {
          frame = 0;
        } else {
          frame = frame - 10;
        }
        player?.video?.seekToFrame(frame);
      }
    });
  };

  const onFfForward = () => {
    if (!player) return;
    player.video.pause().subscribe({
      next: () => {
        let frame = player?.video?.getCurrentFrame() || 0;
        const totalFrames = player?.video?.getVideo()?.totalFrames;
        if (totalFrames && frame + 10 >= totalFrames) {
          frame = totalFrames;
        } else {
          frame = frame + 10;
        }
        player?.video?.seekToFrame(frame);
      }
    });
  };

  const onBack = () => {
    if (!player) return;
    player?.video?.pause()?.subscribe({
      next: () => {
        player?.video?.seekPreviousFrame();
      }
    });
  };

  const onForward = () => {
    if (!player) return;
    player.video.pause().subscribe({
      next: () => {
        let frame = player?.video?.getCurrentFrame() || 0;
        const totalFrames = player?.video?.getVideo()?.totalFrames || 0;
        if (totalFrames && frame + 1 >= totalFrames) {
          frame = totalFrames;
        } else {
          frame = frame + 1;
        }
        player?.video?.seekToFrame(frame);
      }
    });
  };

  const onTypeChanged = (e: SelectChangeEvent<string>) => {
    const value = e.target.value as string;
    if (value === 'all') {
      lanes.forEach((components) => {
        components.forEach((component: any) => {
          component?.maximize();
        });
      });
    } else {
      lanes.get(value)?.forEach((component: any) => {
        if (Array.isArray(component)) {
          component[0]?.maximize();
        } else {
          component?.maximize();
        }
      });
      [...lanes]
        .filter(([key]) => key !== value)
        .forEach(([, components]) => {
          components.forEach((component: any) => {
            if (Array.isArray(component)) {
              component?.forEach((c) => c?.minimize());
            } else {
              component?.minimize();
            }
          });
        });
    }
  };

  const onPlayVideo = () => {
    if (!player) return;
    player.video.togglePlayPause();
  };

  const onPlaybackRateChange = (rate: number) => {
    if (!player) return;
    player.video.setPlaybackRate(rate).subscribe();
  };

  const onFullScreen = () => {
    if (!player) return;
    player.video.toggleFullscreen();
  };

  const onZoomIn = () => {
    if (!player) return;
    player.timeline?.zoomInEased();
  };

  const onZoomOut = () => {
    if (!player) return;
    player.timeline?.zoomOutEased();
  };

  const closeMarkerModal = useCallback(() => {
    setShowMarkerModal(false);
    setMarkerModalTrigger('control');
    setMarkerModalData(undefined);
  }, []);

  const clearFocusedMarker = useCallback(() => {
    setFocusedMarkerId(undefined);
  }, []);

  const handleMarkerSave = (marker: CustomMarker) => {
    setCustomMarkers((prev) => [...prev, marker]);
    setFocusedMarkerId(marker.id);
    closeMarkerModal();
  };

  const onOpenMarkerModalFromControl = () => {
    clearFocusedMarker();
    setMarkerModalTrigger('control');
    setMarkerModalData(undefined);
    setShowMarkerModal(true);
  };

  const onSetInMarker = useCallback(() => {
    if (!player) return;

    const currentTime = player.video.getCurrentTime();
    if (focusedMarkerId) {
      setCustomMarkers((prev) => {
        return prev.map((marker) => {
          if (marker.id !== focusedMarkerId) {
            return marker;
          }

          return {
            ...marker,
            start: currentTime,
            end: Math.max(currentTime, marker.end)
          };
        });
      });
      return;
    }

    setMarkerModalTrigger('in-keypress');
    setMarkerModalData({
      id: '',
      label: '',
      url: url || '',
      start: currentTime,
      end: currentTime,
      color: '#3D9BFF',
      comments: ''
    });
    setShowMarkerModal(true);
  }, [player, focusedMarkerId, url]);

  const onSetOutMarker = useCallback(() => {
    if (!player) return;

    if (!focusedMarkerId) {
      error('Focus a marker first, or press "i" to create one.');
      return;
    }

    const currentTime = player.video.getCurrentTime();
    setCustomMarkers((prev) => {
      return prev.map((marker) => {
        if (marker.id !== focusedMarkerId) {
          return marker;
        }

        return {
          ...marker,
          end: Math.max(marker.start, currentTime)
        };
      });
    });
  }, [player, focusedMarkerId, error]);

  const onSeekToMarkerStart = useCallback(() => {
    if (!player) return;

    const activeMarker = customMarkers.find((marker) => marker.id === focusedMarkerId);
    if (!activeMarker) {
      error('Focus a marker first.');
      return;
    }

    player.video.seekToTime(activeMarker.start);
  }, [player, customMarkers, focusedMarkerId, error]);

  const onSeekToMarkerEnd = useCallback(() => {
    if (!player) return;

    const activeMarker = customMarkers.find((marker) => marker.id === focusedMarkerId);
    if (!activeMarker) {
      error('Focus a marker first.');
      return;
    }

    player.video.seekToTime(activeMarker.end);
  }, [player, customMarkers, focusedMarkerId, error]);

  useEffect(() => {
    if (!focusedMarkerId) return;

    if (!customMarkers.some((marker) => marker.id === focusedMarkerId)) {
      setFocusedMarkerId(undefined);
    }
  }, [focusedMarkerId, customMarkers]);

  useEffect(() => {
    const handleClearFocus = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTypingTarget(event.target)) {
        return;
      }

      if (event.ctrlKey && event.key.toLowerCase() === 'c' && focusedMarkerId) {
        event.preventDefault();
        clearFocusedMarker();
      }
    };

    document.addEventListener('keydown', handleClearFocus);

    return () => {
      document.removeEventListener('keydown', handleClearFocus);
    };
  }, [focusedMarkerId, clearFocusedMarker]);

  useEffect(() => {
    if (!hasCustomMarkers) return;

    const handleInOutShortcuts = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || isTypingTarget(event.target)) {
        return;
      }

      if (showMarkerModal) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === 'i') {
        event.preventDefault();
        onSetInMarker();
      }

      if (key === 'o') {
        event.preventDefault();
        onSetOutMarker();
      }
    };

    document.addEventListener('keydown', handleInOutShortcuts);

    return () => {
      document.removeEventListener('keydown', handleInOutShortcuts);
    };
  }, [hasCustomMarkers, onSetInMarker, onSetOutMarker, showMarkerModal]);

  // --- Sidecar audio fallback UI ---
  // Fallback audio selection: include 'Embedded' as first option
  const [fallbackAudioIdx, setFallbackAudioIdx] = useState(0);
  const sidecarAudioFiles = useMemo(() => externalAssets.filter((a) => getFileGroup(a.file_type) === 'audio'), [externalAssets]);
  const fallbackAudioOptions = useMemo(
    () => [
      { label: 'Embedded', value: 'embedded' },
      ...sidecarAudioFiles.map((track, idx) => ({ label: track.name || track.url || `Audio ${idx + 1}`, value: idx }))
    ],
    [sidecarAudioFiles]
  );
  // If there are sidecar audio files, but no visualization (audio) timeline lanes, show fallback UI
  const showSidecarAudioFallback = useMemo(() => {
    const hasAudioTracksAndVisualizations = audioTracks?.length && audioTracks?.some((track) => track.visual_reference.length > 0);
    if (!player || sidecarAudioFiles.length === 0 || hasAudioTracksAndVisualizations) return false;
    // Check for visualization audio lanes in the timeline
    const timeline = player.timeline;
    if (!timeline) return true;
    const lanes = timeline.getTimelineLanes ? timeline.getTimelineLanes() : [];
    // AudioTrackLane is the visualization lane for audio
    const hasAudioVisualization = lanes.some((lane) => lane.constructor && lane.constructor.name === 'AudioTrackLane');
    return !hasAudioVisualization;
  }, [player, sidecarAudioFiles, audioTracks]);

  // When fallback UI is used, switch audio by removing all and adding the selected sidecar
  useEffect(() => {
    if (!player || !showSidecarAudioFallback) return;
    if (fallbackAudioIdx === 0) {
      // Embedded selected
      player.audio.removeAllSidecarAudioTracks();
    } else {
      // Sidecar selected
      player.audio.removeAllSidecarAudioTracks();
      const selected = sidecarAudioFiles[fallbackAudioIdx - 1];
      if (selected) {
        player.audio.createSidecarAudioTrack({
          src: selected.proxy?.url || selected.url,
          label: selected.name,
          active: true
        });
      }
    }
  }, [player, showSidecarAudioFallback, fallbackAudioIdx, sidecarAudioFiles]);

  return (
    <>
      <Grid container>
        <Box sx={{ width: '100%', position: 'relative' }}>
          {!isPlaying && resolvedPoster && <PosterOverlay src={resolvedPoster} alt="Video poster" />}
          <StyledVideoPlayerDiv ref={containerRef} id={id} onClick={handleClick}>
            {!!hasError && (
              <StyledPlayerErrorDiv>
                <PolarisIcon icon="error" height={20} style={{ marginRight: 10 }} />
                <div>Unable to load video</div>
              </StyledPlayerErrorDiv>
            )}
          </StyledVideoPlayerDiv>
        </Box>
        {showSidecarAudioFallback && (
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
            <span style={{ fontWeight: 500 }}>Audio Track:</span>
            <Select value={fallbackAudioIdx} onChange={(e) => setFallbackAudioIdx(Number(e.target.value))} size="small" sx={{ minWidth: 200 }}>
              {fallbackAudioOptions.map((opt, idx) => (
                <MenuItem key={opt.label} value={idx}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
        <ControlsContainer sx={{ py: 0.5 }} container item id="controls" className="controls prevent-select">
          <Grid item xs={3}>
            <SpeedLabel>Speed</SpeedLabel>
            <SpeedSelect defaultValue="1" size="small" IconComponent={UpDownIcon} onChange={(e) => onPlaybackRateChange(Number(e.target.value))}>
              <MenuItem value="0.25">0.25x</MenuItem>
              <MenuItem value="0.5">0.5x</MenuItem>
              <MenuItem value="1">1x</MenuItem>
              <MenuItem value="2">2x</MenuItem>
              <MenuItem value="3">3x</MenuItem>
            </SpeedSelect>
          </Grid>
          <Grid item container justifyContent="center" gap={2} xs>
            {config?.features?.includes(OmakasePlayerFeature.CUSTOM_MARKERS) && (
              <ControlItem>
                <ControlImage onClick={onOpenMarkerModalFromControl} src="/images/video-player/marker icon.svg" alt="" />
              </ControlItem>
            )}
            {config?.features?.includes(OmakasePlayerFeature.CUSTOM_MARKERS) && (
              <>
                <ControlItem>
                  <ControlImage onClick={onSetInMarker} src="/images/video-player/uil_brackets-curly-open.svg" alt="" />
                </ControlItem>
                <ControlItem>
                  <ControlImage onClick={onSetOutMarker} src="/images/video-player/uil_brackets-curly-close.svg" alt="" />
                </ControlItem>
                <ControlItem>
                  <ControlImage onClick={onSeekToMarkerStart} src="/images/video-player/curly-arrow-open.svg" alt="" />
                </ControlItem>
              </>
            )}
            <ControlItem>
              <ControlImage onClick={onFfBack} id="ff-back" src="/images/video-player/bi_skip-back-fill.svg" alt="" />
            </ControlItem>
            <ControlItem>
              <ControlImage onClick={onBack} id="back" src="/images/video-player/bi_skip-end-fill.svg" alt="" />
            </ControlItem>
            <ControlItem>
              <ControlImage
                onClick={onPlayVideo}
                src={isPlaying ? '/images/video-player/bi_pause-fill.svg' : '/images/video-player/bi_play-fill.svg'}
                alt=""
              />
            </ControlItem>
            <ControlItem>
              <ControlImage onClick={onForward} id="forward" src="/images/video-player/bi_skip-end-fill-forward.svg" alt="" />
            </ControlItem>
            <ControlItem>
              <ControlImage onClick={onFfForward} id="ff-forward" src="/images/video-player/bi_skip-forward.svg" alt="" />
            </ControlItem>
            {config?.features?.includes(OmakasePlayerFeature.CUSTOM_MARKERS) && (
              <ControlItem>
                <ControlImage onClick={onSeekToMarkerEnd} src="/images/video-player/curly-arrow-close.svg" alt="" />
              </ControlItem>
            )}
          </Grid>
          <Grid item container justifyContent="end" gap={2} xs={3}>
            <VolumeController player={player} />
            <ControlItem>
              <ControlImage onClick={onZoomIn} src="/images/video-player/zoom-in.svg" alt="" />
            </ControlItem>
            <ControlItem>
              <ControlImage onClick={onZoomOut} src="/images/video-player/zoom-out.svg" alt="" />
            </ControlItem>
            <ControlItem>
              <ControlImage onClick={onFullScreen} src="/images/video-player/grid.svg" alt="" />
            </ControlItem>
          </Grid>
        </ControlsContainer>
        <TimelineContainer>
          <Grid container item id={`omakase-timeline-${id}`} />
          <FilterSelect onChange={onTypeChanged} defaultValue="all">
            <MenuItem value="all">All Media</MenuItem>
            <MenuItem value="audio">Audio</MenuItem>
            <MenuItem value="subtitle">Subtitle</MenuItem>
          </FilterSelect>
        </TimelineContainer>
        {!disableInventoryModal && <Button onClick={onShowInventoryModal}>+ source files</Button>}
      </Grid>
      <MarkerEditorModal
        open={showMarkerModal}
        triggerSource={markerModalTrigger}
        data={markerModalData}
        onSave={handleMarkerSave}
        toggleOpen={closeMarkerModal}
      />
    </>
  );
};

export default (props: VideoPlayerProps) => useComponentOverride<VideoPlayerProps>(VideoPlayerAdvanced, 'videoPlayer', props);

const StyledVideoPlayerDiv = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  &::before {
    content: ' ';
    display: block;
    width: 100%;
    padding-bottom: ${(9 / 16) * 100}%;
  }

  &.omakase-player .omakase-player-wrapper {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    aspect-ratio: 16/9 !important;

    .omakase-watermark-wrapper,
    .omakase-button-loading {
      display: none !important;
    }

    video[slot='media'] {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }

    .omakase-error-message {
      display: none;
    }
  }
`;

const StyledPlayerErrorDiv = styled.div`
  position: absolute;
  display: flex;
  line-height: 1.2;
  font-size: 1.2rem;
  top: 0;
  left: 0;
  align-items: end;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 99;
  white-space: nowrap;
  color: #fff;
  padding-bottom: ${(props) => props.theme.spacing(2)};
`;

const PosterOverlay = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => theme.spacing(3)};
  box-sizing: border-box;
  object-fit: contain;
  background: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
      : 'linear-gradient(135deg, #1a1d25 0%, #111216 100%)'};
  z-index: 0;
  pointer-events: none;
`;

const ControlsContainer = styled(Grid)`
  background: ${(props) => props.theme.palette.background.default};
  width: 100%;
  display: flex;
`;

const ControlItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ControlImage = styled.img`
  cursor: pointer;
`;

const SpeedLabel = styled.div`
  font-family: 'DM Sans';
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  color: ${(props) => props.theme.palette.default.main};
  background: ${(props) => props.theme.palette.background.default};
  width: fit-content;
  padding: 0 5px;
  display: inline-block;
  height: 18px;
  line-height: 18px;
`;

const SpeedSelect = styled(Select)`
  max-height: 18px;
  line-height: 18px;
  border-radius: 0;
  overflow: hidden;
  & .MuiSelect-select {
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 400;
    color: ${(props) => props.theme.palette.default.main};
    background: ${(props) => props.theme.palette.background.paper};
    border-radius: 0;
    padding-right: 18px !important;
    padding-left: 4px;
  }
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  .MuiSelect-icon {
    top: calc(50% - 5px);
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  width: 100%;

  .omakase-timeline-timecode {
    span {
      color: ${(props) => props.theme.palette.default.main};
    }
  }
`;

const FilterSelect = styled(Select)`
  position: absolute;
  top: 50px;
  left: 10px;
  z-index: 100;
  width: 192px;
  max-height: 29px;
  line-height: 29px;
  border-radius: 4px;
  overflow: hidden;
  & .MuiSelect-select {
    background: ${(props) => props.theme.palette.background.paper};
  }
  fieldset {
    border: 0;
  }
  color: ${(props) => alpha(props.theme.palette.tertiary.contrastText, 0.5)};
  font-family: 'DM Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  svg {
    fill: ${(props) => props.theme.palette.default.light};
  }
`;
